import * as React from 'react';
import type { CountryCode } from 'libphonenumber-js';
import { Input, type InputProps } from './input';
import { countryToFlag } from '../../utils';
import { ValidationStatusIcon, type CheckStatus } from './validation-status';
import { getLoadedPhoneLib, loadPhoneLib } from './phone-lib';

type PhoneLib = NonNullable<ReturnType<typeof getLoadedPhoneLib>>;

/**
 * Validate an international phone number string. Async because the metadata
 * bundle is code-split — see `./phone-lib`.
 */
async function validatePhone(raw: string): Promise<boolean> {
  if (!raw || raw.replace(/[^0-9]/g, '').length < 8) return false;
  const { isValidPhoneNumber } = await loadPhoneLib();
  try {
    return isValidPhoneNumber(raw, 'CH');
  } catch {
    return false;
  }
}

/** Keep only digits and a leading `+`. */
function stripPhone(value: string): string {
  const hasPlus = value.startsWith('+');
  const digits = value.replace(/[^0-9]/g, '');
  return hasPlus ? `+${digits}` : digits;
}

/** Format a raw phone string for display; unchanged until the lib is loaded. */
function formatPhone(
  lib: PhoneLib | null,
  raw: string,
  defaultCountry: CountryCode,
): string {
  if (!raw || !lib) return raw;
  return new lib.AsYouType(defaultCountry).input(raw);
}

/** Detect country code from a phone string. */
function detectCountry(
  lib: PhoneLib | null,
  raw: string,
  defaultCountry: CountryCode,
): CountryCode {
  if (!lib || !raw || raw.replace(/[^0-9]/g, '').length < 2) {
    return defaultCountry;
  }
  try {
    return lib.parsePhoneNumber(raw, defaultCountry)?.country ?? defaultCountry;
  } catch {
    return defaultCountry;
  }
}

/** Convert raw input to E.164 when possible. */
function toE164(
  lib: PhoneLib | null,
  raw: string,
  defaultCountry: CountryCode,
): string {
  if (!raw || !lib) return raw;
  try {
    const parsed = lib.parsePhoneNumber(raw, defaultCountry);
    if (parsed) return parsed.number;
  } catch {
    // fall through
  }
  return raw;
}

export interface PhoneInputProps extends Omit<
  InputProps,
  | 'value'
  | 'onChange'
  | 'type'
  | 'maxLength'
  | 'endAdornment'
  | 'startAdornment'
> {
  /** E.164 phone value (e.g. "+41791234567") */
  value?: string;
  /** Called with the raw E.164 string */
  onChange?: (value: string) => void;
  /** Default country for numbers without a `+` prefix. Defaults to CH. */
  defaultCountry?: CountryCode;
}

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    { value: controlledValue, onChange, defaultCountry = 'CH', ...props },
    ref,
  ) => {
    const [internalValue, setInternalValue] = React.useState('');
    const isControlled = controlledValue !== undefined;
    const rawValue = isControlled ? controlledValue : internalValue;

    const [status, setStatus] = React.useState<CheckStatus>({ type: 'idle' });
    // Starts null on first paint (the metadata chunk is still in flight), so
    // the field shows the unformatted value for a beat before reformatting.
    const [lib, setLib] = React.useState(getLoadedPhoneLib);

    React.useEffect(() => {
      if (lib) return;
      let cancelled = false;
      loadPhoneLib().then((loaded) => {
        if (!cancelled) setLib(loaded);
      });
      return () => {
        cancelled = true;
      };
    }, [lib]);

    const digitCount = rawValue.replace(/[^0-9]/g, '').length;
    const country = detectCountry(lib, rawValue, defaultCountry);

    React.useEffect(() => {
      if (digitCount < 8) {
        setStatus({ type: 'idle' });
        return;
      }
      let cancelled = false;
      validatePhone(rawValue).then((valid) => {
        if (!cancelled) setStatus({ type: valid ? 'valid' : 'invalid' });
      });
      return () => {
        cancelled = true;
      };
    }, [rawValue, digitCount]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const stripped = stripPhone(e.target.value);
      const e164 = toE164(lib, stripped, defaultCountry);

      if (!isControlled) setInternalValue(e164);
      onChange?.(e164);
    };

    const flagEmoji = (
      <span className="text-base leading-none">{countryToFlag(country)}</span>
    );

    const statusIcon =
      status.type !== 'idle' ? (
        <ValidationStatusIcon status={status} />
      ) : undefined;

    return (
      <Input
        ref={ref}
        value={formatPhone(lib, rawValue, defaultCountry)}
        onChange={handleChange}
        placeholder="+41 79 123 45 67"
        inputMode="tel"
        autoComplete="tel"
        spellCheck={false}
        aria-invalid={status.type === 'invalid' || undefined}
        startAdornment={flagEmoji}
        endAdornment={statusIcon}
        {...props}
        className={props.className}
        style={{
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.05em',
          ...props.style,
        }}
      />
    );
  },
);
PhoneInput.displayName = 'PhoneInput';

export { PhoneInput, validatePhone };
